/**
 * 场景补充说明 · 可编辑文本区域
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';

interface Props {
  episodeId: string;
  episodeIndex: number;
  sceneId: string;
  initialNotes: string;
}

export function SceneNotesField({
  episodeId,
  episodeIndex,
  sceneId,
  initialNotes,
}: Props) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initialNotes);

  useEffect(() => {
    setValue(initialNotes);
  }, [initialNotes, sceneId]);

  const saveMut = useMutation({
    mutationFn: async () =>
      api
        .patch(
          `director/episodes/${episodeId}/director/episodes/${episodeIndex}/scenes/${sceneId}`,
          { json: { moreNotes: value } },
        )
        .json<{ sceneId: string; moreNotes: string | null }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('补充说明已保存');
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  const dirty = value !== initialNotes;

  return (
    <section className="rounded-lg border border-outline-variant/30 bg-white px-3 py-2.5">
      <header className="mb-1.5 flex items-center justify-between">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          补充说明
        </h4>
        {dirty && (
          <Button
            size="sm"
            variant="agent"
            agentColor="director"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            保存
          </Button>
        )}
      </header>
      <textarea
        className="w-full resize-none rounded-md border border-outline-variant/30 bg-surface-variant/10 px-2 py-1.5 text-[12px] leading-relaxed text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-200"
        rows={3}
        placeholder="可为 AI 补充额外的场景说明、拍摄要求、特殊注意事项…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </section>
  );
}
